import React, { useState } from "react";
import {
  Box,
  Typography,
  Link,
  Divider,
  Alert,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, Google } from "@mui/icons-material";

import logo from "../../assets/nwt.png";
import { useLoginMutation, useSsoLoginMutation } from "../../services";
import { LoginProps } from "./types";
import {
  BrandIcon,
  BrandSection,
  GoogleButton,
  Card,
  LoginContainer,
  StyledTextField,
} from "./styles";
import Button, { ButtonTypes } from "../../components/Button";
import { IconButton } from "../../components/IconButton";

export const LoginPage: React.FC<LoginProps> = ({ getUser }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [login] = useLoginMutation();
  const [ssoLogin] = useSsoLoginMutation();

  const handleLogin = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!email || !password) {
      setError("Please fill in all fields");
      setLoading(false);
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      await login({ email, password }).unwrap();
      getUser();
      globalThis.window.location.href = "/";
    } catch {
      setError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const url = await ssoLogin().unwrap();
      globalThis.window.location.href = url;
    } catch {
      setError("Couldn't sign in with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <Card elevation={0}>
        <BrandSection>
          <BrandIcon>
            <img src={logo} alt="NWT" />
          </BrandIcon>
          <Typography variant="h4" sx={{ fontWeight: "bold" }} gutterBottom>
            Net Worth Tracker
          </Typography>
          <Typography variant="body1" color="text.secondary">
            See your financial picture clearly
          </Typography>
        </BrandSection>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin}>
          <StyledTextField
            fullWidth
            label="Email Address"
            type="email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
            autoFocus
          />

          <StyledTextField
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="current-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Box sx={{ textAlign: "right", mb: 3 }}>
            <Link
              href="/forgot-password"
              variant="body2"
              sx={{ color: "var(--color-primary)" }}
              underline="hover"
            >
              Forgot your password?
            </Link>
          </Box>

          <Button
            buttonType="submit"
            type={ButtonTypes.primary}
            size="large"
            isLoading={loading}
            isFullWidth={true}
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>

        <GoogleButton
          type={ButtonTypes.cancel}
          size="large"
          isFullWidth
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <Google />
          Continue with Google
        </GoogleButton>

        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{" "}
            <Link
              href="/register"
              sx={{ color: "var(--color-primary)", fontWeight: "medium" }}
              underline="hover"
            >
              Create one now
            </Link>
          </Typography>
        </Box>
      </Card>
    </LoginContainer>
  );
};
